import React, { useMemo, useState } from 'react';
import { useVaccinationAlerts, useMarkVaccinationCompleted } from '../hooks/useVaccinations';
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Vaccines as VaccinesIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as MarkCompleteIcon,
  NotificationImportant as AlertsIcon,
  List as ListIcon,
} from '@mui/icons-material';

interface VaccinationAlert {
  batch: string;
  procurement_id: number;
  delivery_date: string;
  bird_count: number;
  vaccination_key: string;
  target_age: string;
  status: string;
  treatment: string;
}

const getStatusChip = (status: string) => {
  if (status === 'OVERDUE') {
    return (
      <Chip
        label="Overdue"
        size="small"
        sx={{ backgroundColor: '#d32f2f', color: 'white', fontWeight: 600 }}
      />
    );
  }
  if (status === 'DUE NOW') {
    return (
      <Chip
        label="Due Now"
        size="small"
        sx={{ backgroundColor: '#e65100', color: 'white', fontWeight: 600 }}
      />
    );
  }
  if (status === 'UPCOMING') {
    return (
      <Chip
        label="Upcoming"
        size="small"
        sx={{ backgroundColor: '#1565c0', color: 'white', fontWeight: 600 }}
      />
    );
  }
  return (
    <Chip
      label="Scheduled"
      size="small"
      sx={{
        backgroundColor: 'rgba(255,255,255,0.08)',
        color: 'rgba(255,255,255,0.5)',
        fontWeight: 600,
      }}
    />
  );
};

const VaccinationAlerts: React.FC = () => {
  const [confirmDialog, setConfirmDialog] = useState<VaccinationAlert | null>(null);

  const { data: alerts, isFetching, error, refetch } = useVaccinationAlerts();

  const mutation = useMarkVaccinationCompleted();

  const handleMarkAsCompleted = (
    procurementId: number,
    vaccinationKey: string,
    deliveryDate: string
  ) => {
    mutation.mutate(
      { procurementId, vaccinationKey, deliveryDate },
      {
        onSuccess: () => {
          setConfirmDialog(null);
        },
      }
    );
  };

  const stats = useMemo(() => {
    const data = alerts || [];
    return {
      total: data.length,
      overdue: data.filter((a) => a.status === 'OVERDUE').length,
      dueSoon: data.filter((a) => a.status === 'DUE NOW').length,
      upcoming: data.filter((a) => a.status === 'UPCOMING').length,
    };
  }, [alerts]);

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 3,
            }}
          >
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'transparent !important',
                border: 'none',
                color: '#ffffff',
                padding: 0,
                '& .MuiAlert-icon': { color: '#f44336' },
                '& .MuiAlert-message': { color: '#ffffff', padding: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff !important' }}>
                Failed to Load Vaccination Alerts
              </Typography>
              <Typography
                variant="body2"
                paragraph
                sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}
              >
                {error instanceof Error
                  ? error.message
                  : 'An error occurred while fetching vaccination alerts.'}
              </Typography>
            </Alert>
          </Paper>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    {
      field: 'batch',
      headerName: 'Batch',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            color: '#ff9800',
            fontWeight: 600,
            border: '1px solid rgba(255,152,0,0.3)',
          }}
        />
      ),
    },
    {
      field: 'treatment',
      headerName: 'Treatment / Vaccine',
      flex: 1.5,
      minWidth: 220,
    },
    {
      field: 'target_age',
      headerName: 'Target Age',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => getStatusChip(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          title="Mark as Completed"
          onClick={() => setConfirmDialog(params.row as VaccinationAlert)}
          disabled={mutation.isPending}
          sx={{ color: '#4caf50', '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' } }}
        >
          <MarkCompleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page Header Banner */}
      <Box
        sx={{
          mb: 3.5,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #161616 0%, #1f1f1f 100%)',
          border: '1px solid rgba(255,152,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,167,38,0.15)', color: '#ffa726', width: 52, height: 52 }}>
          <AlertsIcon fontSize="medium" />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: '#ffffff', letterSpacing: '-0.01em' }}
          >
            Vaccination Alerts
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
            Monitor overdue, upcoming, and scheduled vaccinations across all batches
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(255,152,0,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(255,152,0,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,152,0,0.12)',
                  color: '#ff9800',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <VaccinesIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  TOTAL ALERTS
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#ff9800', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.total}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  All pending vaccinations
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(244,67,54,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(244,67,54,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(244,67,54,0.12)',
                  color: '#f44336',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <WarningIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  OVERDUE
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#f44336', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.overdue}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  Requires immediate action
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(245,124,0,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(245,124,0,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(245,124,0,0.12)',
                  color: '#f57c00',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <ScheduleIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  DUE NOW
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#f57c00', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.dueSoon}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  Administer today
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
              border: '1px solid rgba(21,101,192,0.15)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                border: '1px solid rgba(21,101,192,0.35)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(21,101,192,0.12)',
                  color: '#1976d2',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <CheckCircleIcon />
              </Avatar>
              <Box flex={1}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    letterSpacing: '0.8px',
                    color: 'rgba(255,152,0,0.7)',
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  UPCOMING
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: '#1976d2', lineHeight: 1.2, mt: 0.25 }}
                >
                  {stats.upcoming}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem' }}
                >
                  Within 5 days
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper
        sx={{ p: 0, border: '1px solid rgba(255,152,0,0.2)', borderRadius: 2, overflow: 'hidden' }}
      >
        {/* Table Section Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg, #161616 0%, #1e1e1e 100%)',
            borderBottom: '1px solid rgba(255,152,0,0.15)',
          }}
        >
          <ListIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff' }}>
            Vaccination Schedule
          </Typography>
          <Chip
            label={alerts?.length ?? 0}
            size="small"
            sx={{ bgcolor: 'rgba(255,152,0,0.12)', color: '#ff9800', fontWeight: 700, height: 20 }}
          />
        </Box>
        <DataGrid
          rows={alerts || []}
          columns={columns}
          getRowId={(row) => `${row.procurement_id}-${row.vaccination_key}`}
          autoHeight
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(255, 152, 0, 0.08)',
              borderBottom: '1px solid rgba(255, 152, 0, 0.2)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '0.8px',
              color: 'rgba(255,152,0,0.85)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.05)',
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'rgba(255, 255, 255, 0.05)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255, 152, 0, 0.15)',
            },
          }}
        />
      </Paper>

      {/* Confirm Mark as Completed Dialog */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>Confirm Vaccination Completion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Mark <strong style={{ color: '#ff9800' }}>{confirmDialog?.treatment}</strong> as
            completed for batch <strong style={{ color: '#ff9800' }}>{confirmDialog?.batch}</strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog(null)}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              confirmDialog &&
              handleMarkAsCompleted(
                confirmDialog.procurement_id,
                confirmDialog.vaccination_key,
                confirmDialog.delivery_date
              )
            }
            disabled={mutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)' },
            }}
          >
            {mutation.isPending ? 'Saving...' : 'Mark as Completed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VaccinationAlerts;
